import { useEffect, useState } from 'react';
import { useSupabase } from '@/contexts/supabase/SupabaseClientProvider';
import { useAuth } from '@/hooks/use-auth';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Mail, Phone, User } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface ContactInfo {
  userId: string;
  firstName: string;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  avatarUrl: string | null;
}

interface ContactInfoSheetProps {
  conversationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ContactInfoSheet({ conversationId, open, onOpenChange }: ContactInfoSheetProps) {
  const supabase = useSupabase();
  const { user } = useAuth();
  const [contacts, setContacts] = useState<ContactInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    async function fetchContactInfo() {
      setIsLoading(true);
      setError(null);

      try {
        // Fetch participants for this conversation
        const { data: participants, error: participantsError } = await supabase
          .from('conversation_participants')
          .select('user_id')
          .eq('conversation_id', conversationId);

        if (participantsError) throw participantsError;

        if (!participants || participants.length === 0) {
          setContacts([]);
          return;
        }

        // Get participant IDs, excluding current user
        const participantIds = participants
          .map(p => p.user_id)
          .filter(id => id !== user?.id);

        if (participantIds.length === 0) {
          setContacts([]);
          return;
        }

        // Convert UUIDs to strings for profiles_public_v (user_id is text type in view)
        const participantIdStrings = participantIds.map(id => String(id));

        // Fetch contact details from profiles_public_v
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles_public_v')
          .select('user_id, first_name, last_name, email, phone')
          .in('user_id', participantIdStrings);

        if (profilesError) throw profilesError;

        console.log('[ContactInfo] Fetched profiles:', profiles);

        // Fetch email, phone, and avatars from profiles table
        const { data: profileData } = await supabase
          .from('profiles')
          .select('user_id, avatar_url, email, phone')
          .in('user_id', participantIds);

        console.log('[ContactInfo] Fetched profile data:', profileData);

        const avatarMap = new Map(profileData?.map(a => [a.user_id, a.avatar_url]) || []);
        const emailMap = new Map(profileData?.map(a => [a.user_id, a.email]) || []);
        const phoneMap = new Map(profileData?.map(a => [a.user_id, a.phone]) || []);

        const contactList: ContactInfo[] = (profiles || []).map(p => ({
          userId: p.user_id || '',
          firstName: p.first_name || 'Unknown',
          lastName: p.last_name,
          email: p.email || emailMap.get(p.user_id || '') || null,
          phone: p.phone || phoneMap.get(p.user_id || '') || null,
          avatarUrl: avatarMap.get(p.user_id || '') || null,
        }));

        setContacts(contactList);
      } catch (err) {
        console.error('Error fetching contact info:', err);
        setError('Failed to load contact information');
      } finally {
        setIsLoading(false);
      }
    }

    fetchContactInfo();
  }, [conversationId, open, user?.id, supabase]);

  const getInitials = (firstName: string, lastName: string | null) => {
    return `${firstName.charAt(0)}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Contact Information</SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {isLoading && (
            <>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Skeleton className="h-12 w-12 rounded-full" />
                    <Skeleton className="h-5 w-32" />
                  </div>
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-3/4" />
                </CardContent>
              </Card>
            </>
          )}

          {error && (
            <div className="text-sm text-destructive text-center py-4">
              {error}
            </div>
          )}

          {!isLoading && !error && contacts.length === 0 && (
            <div className="text-sm text-muted-foreground text-center py-8">
              <User className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No contact information available</p>
            </div>
          )}

          {!isLoading && contacts.map((contact) => (
            <Card key={contact.userId}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <Avatar className="h-12 w-12">
                    {contact.avatarUrl && (
                      <AvatarImage src={contact.avatarUrl} alt={contact.firstName} />
                    )}
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {getInitials(contact.firstName, contact.lastName)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-semibold text-foreground">
                      {contact.firstName} {contact.lastName || ''}
                    </h3>
                  </div>
                </div>

                <div className="space-y-2">
                  {contact.email ? (
                    <a
                      href={`mailto:${contact.email}`}
                      className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Mail className="h-4 w-4 shrink-0" />
                      <span className="break-all">{contact.email}</span>
                    </a>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground/50">
                      <Mail className="h-4 w-4 shrink-0" />
                      <span>No email</span>
                    </div>
                  )}

                  {contact.phone ? (
                    <a
                      href={`tel:${contact.phone}`}
                      className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Phone className="h-4 w-4 shrink-0" />
                      <span>{contact.phone}</span>
                    </a>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground/50">
                      <Phone className="h-4 w-4 shrink-0" />
                      <span>No phone</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
